import { useEffect, useState } from "react";
import { useAuthStore, api } from "@/store";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { HelpCircle, Pencil, Save, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";

function renderMarkdown(text) {
  if (!text) return "";
  const lines = text.split("\n");
  let html = "";
  let inList = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (inList) { html += "</ul>"; inList = false; }
      continue;
    }
    let processed = trimmed
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\{(\w+)\}/g, '<code class="px-1 py-0.5 bg-slate-100 rounded text-xs">{$1}</code>');

    if (trimmed.startsWith("# ")) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h1 class="text-xl font-bold text-[#14532d] mb-3 mt-6">${processed.slice(2)}</h1>`;
    } else if (trimmed.startsWith("## ")) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h2 class="text-base font-semibold text-[#14532d] mb-2 mt-5 border-b border-emerald-100 pb-1">${processed.slice(3)}</h2>`;
    } else if (trimmed.startsWith("- ")) {
      if (!inList) { html += '<ul class="list-disc pl-6 mb-3 space-y-1">'; inList = true; }
      html += `<li class="text-sm text-slate-700 leading-relaxed">${processed.slice(2)}</li>`;
    } else {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<p class="text-sm text-slate-700 mb-2 leading-relaxed">${processed}</p>`;
    }
  }
  if (inList) html += "</ul>";
  return html;
}

export default function InstructionsPage() {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const [content, setContent] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get("/instructions");
      setContent(res.data.content || "");
    } catch { toast.error(t("loadFailed")); }
    finally { setLoading(false); }
  };

  const startEdit = () => { setEditContent(content); setEditing(true); };
  const cancelEdit = () => { setEditing(false); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/instructions", { content: editContent });
      setContent(editContent);
      setEditing(false);
      toast.success(t("instructionsSaved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div data-testid="instructions-page" className="max-w-3xl mx-auto py-8 px-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#14532d]/10 flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-[#14532d]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#14532d]">{t("instructions")}</h1>
            <p className="text-xs text-slate-500">{t("instructionsSubtitle")}</p>
          </div>
        </div>
        {isAdmin() && !editing && (
          <Button data-testid="edit-instructions" variant="outline" size="sm" onClick={startEdit}
            className="text-[#14532d] border-emerald-200 hover:bg-emerald-50">
            <Pencil className="w-4 h-4 mr-1" /> {t("editInstructions")}
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={cancelEdit}>
              <X className="w-4 h-4 mr-1" /> {t("cancel")}
            </Button>
            <Button size="sm" onClick={save} disabled={saving}
              className="bg-[#14532d] hover:bg-[#166534] text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {t("save")}
            </Button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-400">{t("markdownHint")}</p>
          <Textarea data-testid="instructions-editor" value={editContent}
            onChange={e => setEditContent(e.target.value)}
            className="min-h-[500px] font-mono text-sm leading-relaxed"
            placeholder="# Заглавие&#10;## Подзаглавие&#10;- Точка 1&#10;- Точка 2" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />
      )}
    </div>
  );
}
